import { RoomType } from 'src/types/cartography.types';

import { ROOM_CATEGORIES } from './roomCategories';
import { ROOM_TYPE_ALIASES } from './roomTypeAliases';

export interface RoomTypeMatch {
  type: RoomType;
  /** Alias ayant provoqué la correspondance, quand ce n'est pas le libellé canonique. */
  matchedAlias?: string;
}

/** Minuscule, sans accent : « Véranda » et « veranda » doivent se valoir. */
export const normalize = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const ALL_TYPES: RoomType[] = ROOM_CATEGORIES.flatMap(category => category.types);

/**
 * Cherche un type de pièce par libellé ou par alias régional. Les correspondances en
 * début de chaîne remontent avant les correspondances au milieu, pour que « cave »
 * propose Cave avant Cave à vin.
 *
 * @param labelOf rend le libellé traduit d'un type — injecté pour rester testable hors i18n.
 */
export const searchRoomTypes = (
  query: string,
  labelOf: (type: RoomType) => string
): RoomTypeMatch[] => {
  const needle = normalize(query);
  if (needle.length === 0) return [];

  const matches: Array<RoomTypeMatch & { rank: number }> = [];

  for (const type of ALL_TYPES) {
    const label = normalize(labelOf(type));
    const labelIndex = label.indexOf(needle);
    if (labelIndex === 0) {
      matches.push({ type, rank: 0 });
      continue;
    }
    if (labelIndex > 0) {
      matches.push({ type, rank: 1 });
      continue;
    }

    const alias = (ROOM_TYPE_ALIASES[type] ?? []).find(candidate =>
      normalize(candidate).includes(needle)
    );
    if (alias) {
      matches.push({ type, matchedAlias: alias, rank: 2 });
    }
  }

  return matches
    .sort((a, b) => a.rank - b.rank)
    .map(({ type, matchedAlias }) => ({ type, matchedAlias }));
};
